import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EmbeddedAuthDialog } from '@/components/embedded/EmbeddedAuthDialog';
import { publishPlatformEvent } from '@/platform/events';
import { authMessageBridge } from '@/platform/authentication/AuthMessageBridge';
import { embeddedAuthService } from '@/platform/authentication/EmbeddedAuthService';
import {
  isSameEmbeddedProduct,
  normalizeEmbeddedProductId,
} from '@/platform/authentication/embeddedProductId';
import {
  EmbeddedAuthContext,
  type EmbeddedAuthContextValue,
} from '@/platform/authentication/EmbeddedAuthContext';
import { embeddedCommunicationService } from '@/services/embeddedCommunicationService';
import type {
  EmbeddedAuthIframeMessage,
  EmbeddedAuthRequestPayload,
  EmbeddedAuthStatePayload,
} from '@/types/embeddedApplication';

interface EmbeddedFrameRegistration {
  productId: string;
  productName: string;
  iframeWindow: Window;
  trustedOrigin: string;
  requestedScopes?: string[];
}

interface PendingAuthRequest {
  message: EmbeddedAuthIframeMessage;
  origin: string;
}

const toPendingRequestKey = (productId: string, origin: string): string => {
  return `${normalizeEmbeddedProductId(productId)}|${origin}`;
};

interface DialogState {
  open: boolean;
  phase: 'idle' | 'loading' | 'error';
  errorMessage?: string;
}

const parentLogoutEventName = 'entity-portal:auth-logout';
const embeddedLogoutEventName = 'entity-portal:embedded-product-logout';

function resolveAuthRequest(
  registration: EmbeddedFrameRegistration,
  request: EmbeddedAuthRequestPayload,
): EmbeddedAuthRequestPayload {
  const resolvedProductId = normalizeEmbeddedProductId(registration.productId);

  return {
    ...request,
    productId: resolvedProductId,
  };
}

function toEventEnvironment(): string {
  return import.meta.env.MODE ?? 'local';
}

function toMessage(value: unknown): string {
  return value instanceof Error
    ? value.message
    : 'Embedded authentication failed.';
}

function nextCorrelationId(): string {
  return typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseDebugBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
  }

  return false;
}

function isEmbeddedAuthDebugEnabled(): boolean {
  return (
    parseDebugBoolean(import.meta.env.VITE_EMBEDDED_AUTH_DEBUG) ||
    parseDebugBoolean(window.environment?.embeddedAuth?.debug)
  );
}

export function EmbeddedAuthProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('entity-portal');
  const debugEnabled = useMemo(() => isEmbeddedAuthDebugEnabled(), []);
  const registrationsRef = useRef(new Map<string, EmbeddedFrameRegistration>());
  const pendingAuthRequestsRef = useRef(
    new WeakMap<Window, PendingAuthRequest>(),
  );
  const pendingAuthRequestsByProductRef = useRef(
    new Map<string, PendingAuthRequest>(),
  );
  const refreshTimersRef = useRef(new Map<string, number>());
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    phase: 'idle',
  });
  const [pendingRequest, setPendingRequest] =
    useState<EmbeddedAuthRequestPayload | null>(null);
  const [pendingClientProductId, setPendingClientProductId] = useState<
    string | null
  >(null);
  const [pendingRegistration, setPendingRegistration] =
    useState<EmbeddedFrameRegistration | null>(null);
  const [pendingProductName, setPendingProductName] = useState<string>('');

  const emitDebugTelemetry = useCallback(
    (
      detail: string,
      metadata?: Record<string, string | number | boolean | null>,
    ) => {
      if (!debugEnabled) {
        return;
      }

      window.dispatchEvent(
        new CustomEvent('entity-portal:embedded-auth-debug', {
          detail: {
            detail,
            metadata,
            occurredAt: new Date().toISOString(),
          },
        }),
      );
    },
    [debugEnabled],
  );

  const clearRefreshTimer = useCallback((productId: string) => {
    const timerId = refreshTimersRef.current.get(productId);

    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      refreshTimersRef.current.delete(productId);
    }
  }, []);

  const findRegistrationByWindow = useCallback(
    (
      source: Window,
      origin: string,
      requestedProductId?: string,
    ): EmbeddedFrameRegistration | null => {
      for (const registration of registrationsRef.current.values()) {
        if (
          registration.iframeWindow === source &&
          registration.trustedOrigin === origin
        ) {
          return registration;
        }
      }

      if (!requestedProductId) {
        return null;
      }

      for (const registration of registrationsRef.current.values()) {
        if (
          registration.trustedOrigin === origin &&
          isSameEmbeddedProduct(registration.productId, requestedProductId)
        ) {
          return registration;
        }
      }

      return null;
    },
    [],
  );

  const sendAuthState = useCallback(
    (
      registration: EmbeddedFrameRegistration,
      authState: EmbeddedAuthStatePayload,
    ) => {
      embeddedCommunicationService.sendToIframeWithOrigin(
        registration.iframeWindow,
        registration.trustedOrigin,
        authMessageBridge.toAuthStateMessage(authState),
      );
    },
    [],
  );

  const sendAuthError = useCallback(
    (
      registration: EmbeddedFrameRegistration,
      request: EmbeddedAuthRequestPayload,
      error: unknown,
    ) => {
      const payload = embeddedAuthService.toErrorPayload(request, error);

      embeddedCommunicationService.sendToIframeWithOrigin(
        registration.iframeWindow,
        registration.trustedOrigin,
        authMessageBridge.toAuthErrorMessage(payload),
      );
    },
    [],
  );

  const scheduleRefresh = useCallback(
    (
      registration: EmbeddedFrameRegistration,
      authState: EmbeddedAuthStatePayload,
      requestedScopes?: string[],
    ) => {
      clearRefreshTimer(registration.productId);

      const refreshAt = new Date(authState.expiresAt).getTime() - 2 * 60 * 1000;
      const delayMs = Math.max(refreshAt - Date.now(), 30_000);

      const timerId = window.setTimeout(async () => {
        try {
          const refreshed = await embeddedAuthService.refresh({
            version: 1,
            correlationId: nextCorrelationId(),
            productId: registration.productId,
            requestedScopes,
          });

          sendAuthState(registration, refreshed);
          scheduleRefresh(registration, refreshed, requestedScopes);

          publishPlatformEvent({
            type: 'API_CONFIGURED',
            category: 'embeddedApplications',
            actor: {
              displayName: 'Embedded Auth Service',
              actorType: 'system',
            },
            resource: 'embeddedAuth.refresh',
            details: `Token refresh succeeded for ${registration.productId}.`,
            severity: 'low',
            environment: toEventEnvironment(),
            product: registration.productName,
            ipAddress: '127.0.0.1',
            metadata: {
              auditRequired: false,
            },
          });
        } catch (error) {
          const request = {
            version: 1 as const,
            correlationId: nextCorrelationId(),
            productId: registration.productId,
            requestedScopes,
          };

          sendAuthError(registration, request, error);

          publishPlatformEvent({
            type: 'API_CONFIGURED',
            category: 'embeddedApplications',
            actor: {
              displayName: 'Embedded Auth Service',
              actorType: 'system',
            },
            resource: 'embeddedAuth.refresh',
            details: `Token refresh failed for ${registration.productId}: ${toMessage(error)}`,
            severity: 'medium',
            environment: toEventEnvironment(),
            product: registration.productName,
            ipAddress: '127.0.0.1',
            metadata: {
              auditRequired: false,
            },
          });
        }
      }, delayMs);

      refreshTimersRef.current.set(registration.productId, timerId);
    },
    [clearRefreshTimer, sendAuthError, sendAuthState],
  );

  const processAuthMessage = useCallback(
    async (
      message: EmbeddedAuthIframeMessage,
      registration: EmbeddedFrameRegistration,
    ) => {
      const request = authMessageBridge.toAuthRequestPayload(message);

      if (!isSameEmbeddedProduct(registration.productId, request.productId)) {
        emitDebugTelemetry(
          'Auth request productId differs from frame registration; using registered product.',
          {
            requestedProductId: request.productId,
            registeredProductId: registration.productId,
            resolvedProductId: normalizeEmbeddedProductId(
              registration.productId,
            ),
          },
        );
      }

      const resolvedRequest = resolveAuthRequest(registration, request);

      embeddedCommunicationService.sendToIframeWithOrigin(
        registration.iframeWindow,
        registration.trustedOrigin,
        authMessageBridge.toHandshakeAckMessage({
          version: 1,
          correlationId: request.correlationId,
          productId: request.productId,
          accepted: true,
        }),
      );

      emitDebugTelemetry('Auth request accepted.', {
        productId: resolvedRequest.productId,
        correlationId: request.correlationId,
        type: message.type,
      });

      if (message.type === 'embeddedAuthLogout') {
        embeddedAuthService.clearSession(resolvedRequest.productId);
        clearRefreshTimer(resolvedRequest.productId);
        window.dispatchEvent(new Event(embeddedLogoutEventName));
        emitDebugTelemetry('Embedded auth logout processed.', {
          productId: resolvedRequest.productId,
        });
        return;
      }

      try {
        const authState = await embeddedAuthService.refresh(resolvedRequest);
        sendAuthState(registration, {
          ...authState,
          productId: request.productId,
        });
        scheduleRefresh(
          registration,
          authState,
          resolvedRequest.requestedScopes,
        );
        emitDebugTelemetry('Silent refresh succeeded.', {
          productId: resolvedRequest.productId,
        });
        return;
      } catch (error) {
        emitDebugTelemetry(
          'Silent refresh failed. Escalating to interactive auth.',
          {
            productId: resolvedRequest.productId,
            reason: toMessage(error),
          },
        );
      }

      setPendingRequest(resolvedRequest);
      setPendingClientProductId(request.productId);
      setPendingRegistration(registration);
      setPendingProductName(registration.productName);
      setDialogState({ open: true, phase: 'idle' });
    },
    [clearRefreshTimer, emitDebugTelemetry, scheduleRefresh, sendAuthState],
  );

  const flushPendingAuthRequest = useCallback(
    (registration: EmbeddedFrameRegistration) => {
      const pendingByWindow = pendingAuthRequestsRef.current.get(
        registration.iframeWindow,
      );
      const pendingByProduct = pendingAuthRequestsByProductRef.current.get(
        toPendingRequestKey(registration.productId, registration.trustedOrigin),
      );

      const pending = pendingByWindow ?? pendingByProduct;

      if (!pending) {
        return;
      }

      pendingAuthRequestsRef.current.delete(registration.iframeWindow);
      pendingAuthRequestsByProductRef.current.delete(
        toPendingRequestKey(pending.message.payload.productId, pending.origin),
      );
      emitDebugTelemetry('Flushing pending embedded auth request.', {
        productId: registration.productId,
        type: pending.message.type,
      });

      void processAuthMessage(pending.message, registration);
    },
    [emitDebugTelemetry, processAuthMessage],
  );

  const executeInteractiveAuth = useCallback(async () => {
    if (!pendingRequest || !pendingRegistration) {
      return;
    }

    const registration = pendingRegistration;

    if (!registration) {
      emitDebugTelemetry('Interactive auth requested without registration.', {
        productId: pendingRequest.productId,
      });
      setDialogState({
        open: true,
        phase: 'error',
        errorMessage: t('embedded.embeddedAuthNoActiveFrame'),
      });
      return;
    }

    setDialogState({ open: true, phase: 'loading' });
    emitDebugTelemetry('Interactive auth started.', {
      productId: pendingRequest.productId,
      correlationId: pendingRequest.correlationId,
    });

    try {
      const authState = await embeddedAuthService.authenticate(pendingRequest, {
        interactive: true,
      });

      sendAuthState(registration, {
        ...authState,
        productId: pendingClientProductId ?? pendingRequest.productId,
      });
      scheduleRefresh(registration, authState, pendingRequest.requestedScopes);
      setDialogState({ open: false, phase: 'idle' });
      setPendingRequest(null);
      setPendingClientProductId(null);
      setPendingRegistration(null);
      emitDebugTelemetry('Interactive auth succeeded.', {
        productId: registration.productId,
      });

      publishPlatformEvent({
        type: 'API_CONFIGURED',
        category: 'embeddedApplications',
        actor: {
          displayName: 'Embedded Auth Service',
          actorType: 'system',
        },
        resource: 'embeddedAuth.login',
        details: `Interactive authentication succeeded for ${pendingRequest.productId}.`,
        severity: 'low',
        environment: toEventEnvironment(),
        product: registration.productName,
        ipAddress: '127.0.0.1',
        metadata: {
          auditRequired: false,
        },
      });
    } catch (error) {
      sendAuthError(registration, pendingRequest, error);
      emitDebugTelemetry('Interactive auth failed.', {
        productId: registration.productId,
        reason: toMessage(error),
      });
      setDialogState({
        open: true,
        phase: 'error',
        errorMessage: toMessage(error),
      });

      publishPlatformEvent({
        type: 'API_CONFIGURED',
        category: 'embeddedApplications',
        actor: {
          displayName: 'Embedded Auth Service',
          actorType: 'system',
        },
        resource: 'embeddedAuth.login',
        details: `Interactive authentication failed for ${pendingRequest.productId}: ${toMessage(error)}`,
        severity: 'medium',
        environment: toEventEnvironment(),
        product: registration.productName,
        ipAddress: '127.0.0.1',
        metadata: {
          auditRequired: false,
        },
      });
    }
  }, [
    emitDebugTelemetry,
    pendingClientProductId,
    pendingRegistration,
    pendingRequest,
    scheduleRefresh,
    sendAuthError,
    sendAuthState,
    t,
  ]);

  useEffect(() => {
    const refreshTimers = refreshTimersRef.current;

    const unsubscribe = embeddedCommunicationService.subscribeWithContext(
      async (message, context) => {
        if (!authMessageBridge.isEmbeddedAuthMessage(message)) {
          return;
        }

        const authRequest = authMessageBridge.toAuthRequestPayload(message);

        emitDebugTelemetry('Embedded auth message received.', {
          type: message.type,
          productId: authRequest.productId,
        });

        const registration = findRegistrationByWindow(
          context.source,
          context.origin,
          authRequest.productId,
        );

        if (!registration) {
          pendingAuthRequestsRef.current.set(context.source, {
            message,
            origin: context.origin,
          });
          pendingAuthRequestsByProductRef.current.set(
            toPendingRequestKey(authRequest.productId, context.origin),
            {
              message,
              origin: context.origin,
            },
          );
          emitDebugTelemetry('Auth message queued until frame registration.', {
            origin: context.origin,
            type: message.type,
            productId: authRequest.productId,
          });
          return;
        }

        await processAuthMessage(message, registration);
      },
    );

    return () => {
      unsubscribe();
      refreshTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      refreshTimers.clear();
    };
  }, [emitDebugTelemetry, findRegistrationByWindow, processAuthMessage]);

  useEffect(() => {
    const onParentLogout = () => {
      for (const registration of registrationsRef.current.values()) {
        clearRefreshTimer(registration.productId);
        embeddedAuthService.clearSession(registration.productId);
        embeddedCommunicationService.sendToIframeWithOrigin(
          registration.iframeWindow,
          registration.trustedOrigin,
          {
            type: 'embeddedAuthLogout',
            payload: {
              version: 1,
              correlationId: nextCorrelationId(),
              productId: registration.productId,
            },
          },
        );
      }
    };

    window.addEventListener(parentLogoutEventName, onParentLogout);

    return () => {
      window.removeEventListener(parentLogoutEventName, onParentLogout);
    };
  }, [clearRefreshTimer]);

  const registerEmbeddedFrame = useCallback(
    (registration: EmbeddedFrameRegistration) => {
      const normalizedRegistration: EmbeddedFrameRegistration = {
        ...registration,
        productId: normalizeEmbeddedProductId(registration.productId),
      };

      registrationsRef.current.set(
        normalizedRegistration.productId,
        normalizedRegistration,
      );
      embeddedCommunicationService.registerIframe(
        normalizedRegistration.iframeWindow,
        normalizedRegistration.trustedOrigin,
      );

      emitDebugTelemetry('Embedded frame registered.', {
        productId: normalizedRegistration.productId,
        trustedOrigin: normalizedRegistration.trustedOrigin,
      });

      publishPlatformEvent({
        type: 'API_CONFIGURED',
        category: 'embeddedApplications',
        actor: {
          displayName: 'Embedded Auth Service',
          actorType: 'system',
        },
        resource: 'embeddedAuth.frameRegistered',
        details: `Registered embedded frame for ${normalizedRegistration.productId}.`,
        severity: 'information',
        environment: toEventEnvironment(),
        product: normalizedRegistration.productName,
        ipAddress: '127.0.0.1',
        metadata: {
          auditRequired: false,
        },
      });

      flushPendingAuthRequest(normalizedRegistration);
    },
    [emitDebugTelemetry, flushPendingAuthRequest],
  );

  const unregisterEmbeddedFrame = useCallback(
    (productId: string) => {
      const normalizedProductId = normalizeEmbeddedProductId(productId);
      registrationsRef.current.delete(normalizedProductId);
      clearRefreshTimer(normalizedProductId);
      embeddedAuthService.clearSession(normalizedProductId);
      emitDebugTelemetry('Embedded frame unregistered.', {
        productId: normalizedProductId,
      });
    },
    [clearRefreshTimer, emitDebugTelemetry],
  );

  const closeDialog = useCallback(() => {
    setDialogState({ open: false, phase: 'idle' });
    setPendingRequest(null);
    setPendingClientProductId(null);
    setPendingRegistration(null);
  }, []);

  const contextValue = useMemo<EmbeddedAuthContextValue>(
    () => ({
      registerEmbeddedFrame,
      unregisterEmbeddedFrame,
    }),
    [registerEmbeddedFrame, unregisterEmbeddedFrame],
  );

  return (
    <EmbeddedAuthContext.Provider value={contextValue}>
      {children}
      <EmbeddedAuthDialog
        open={dialogState.open}
        phase={dialogState.phase}
        productName={pendingProductName}
        errorMessage={dialogState.errorMessage}
        onClose={closeDialog}
        onSignIn={executeInteractiveAuth}
        onRetry={executeInteractiveAuth}
      />
    </EmbeddedAuthContext.Provider>
  );
}
